import { SetStateAction, useImperativeHandle, useRef, useState } from 'react'
import { STEPS } from '../constants'
import { trackFunnelViewSlideChanged } from '../services/trackService'
import useMountEffect from './useMountEffect'
import { ViewMode } from '@playbooks/action-cards-utils/src'
import { SliderManager, Step, StepType } from '../types'

const SWITCH_SLIDE_EVENT = 'WP-slide-switch'

function useHorizontalSliderMethods({
  viewMode,
  playbookId,
  sliderManagerRef,
  setProgress,
  setIsFinishButtonVisible,
}: {
  viewMode: ViewMode | undefined
  playbookId: string
  sliderManagerRef: React.RefObject<SliderManager | null>
  setProgress: React.Dispatch<SetStateAction<number>>
  setIsFinishButtonVisible: (flag: boolean) => void
}) {
  const [steps, setSteps] = useState<Step[]>([])
  const scrollContainer = useRef<HTMLDivElement>(null)
  const step = useRef(0)

  useMountEffect(() => {
    if (viewMode !== ViewMode.FUNNEL) return
    if (!scrollContainer.current) return

    let stepCounter = 0
    // to ensure that slides are loaded and we can get the correct number of them
    const scopes = [...scrollContainer.current!.querySelectorAll('[data-separator-scope]')]

    const mappedSteps = scopes.map(x => {
      return {
        type: stepCounter === 0 ? StepType.FIRST : null,
        step: stepCounter++,
        stepsNumber: scopes.length,
        separatorId: Number.parseInt(x.getAttribute('data-separator-scope') || '0') || 0,
      }
    })

    mappedSteps.push({
      type: StepType.LAST,
      step: mappedSteps.length,
      stepsNumber: mappedSteps.length,
      separatorId: 0,
    })

    trackFunnelViewSlideChanged(playbookId, mappedSteps[0].separatorId)
    setSteps(mappedSteps)
  })

  useImperativeHandle(sliderManagerRef, () => {
    return {
      nextSlide: () => {
        triggerSlideSwitchEvent()

        const stepSize = scrollContainer.current?.clientWidth || 0

        scrollContainer.current?.scrollTo({
          left: stepSize * (step.current + STEPS),
          behavior: 'smooth',
        })

        if (steps[step.current].type !== 'last') step.current++

        const currentStepNumber = steps[step.current]

        trackFunnelViewSlideChanged(playbookId, currentStepNumber.separatorId)

        setProgress(prev => {
          const part = 100 / (steps.length - STEPS)
          const res = prev + part
          return res
        })

        setIsFinishButtonVisible(steps[step.current].type === 'last')

        return currentStepNumber
      },
      prevSlide: () => {
        triggerSlideSwitchEvent()

        const stepSize = scrollContainer.current?.clientWidth || 0

        scrollContainer.current?.scrollTo({
          left: stepSize * (step.current - STEPS),
          behavior: 'smooth',
        })

        if (steps[step.current].type !== 'first') step.current--

        setProgress(prev => {
          const part = 100 / (steps.length - STEPS)
          const res = prev - part

          return res <= 0 ? 3 : res
        })
        setIsFinishButtonVisible(steps[step.current].type === 'last')
        return steps[step.current]
      },
      getCurrentStep: () => {
        return steps[step.current]
      },
    }
  })

  return scrollContainer
}

function triggerSlideSwitchEvent() {
  /**
   * `dispatchEvent(new CustomEvent(SWITCH_SLIDE_EVENT))` is needed
   *  to message to the ac lib to stop video/audio playback in recommendations
   */
  dispatchEvent(new CustomEvent(SWITCH_SLIDE_EVENT))
}

export default useHorizontalSliderMethods
